// File: frontend/src/pages/verify-email/VerifyEmailPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resendVerificationEmail } from "../../services/emailService";
import { parseApiError } from "../../utils/parseApiError";
import "./VerifyEmailPage.css";

type VerifyState = "loading" | "success" | "expired" | "invalid" | "error";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

/**
 * Full-page component shown when the user clicks the verification link in their email.
 * Reads the ?token= query param, calls the backend, and shows the result.
 *
 * Route: /email-dogrula?token={uuid}
 *
 * NOTE: useRef guard prevents React StrictMode from running the fetch twice in development.
 * Without it, the first fetch succeeds (sets "success"), then the second fetch fails
 * because the token was already cleared from the DB, overriding the state to "invalid".
 */
export const VerifyEmailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<VerifyState>("loading");

  // ── Expired-link recovery ──
  // An expired token is still the token stored on the account, so it is enough to
  // identify who is asking and hand them a fresh link. Without this the 24-hour
  // expiry is a dead end: the address is already registered so signing up again
  // fails, and login is refused because the account was never verified.
  const [token, setToken] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const [resendDone, setResendDone] = useState(false);

  // Guard: ensures the verification fetch runs exactly once even in React StrictMode,
  // which intentionally mounts → unmounts → remounts components in development.
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Skip the second StrictMode invocation
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      setState("invalid");
      return;
    }

    // Kept in state so the expired branch can ask for a new link without the user
    // having to type anything
    setToken(token);

    // Call the backend verification endpoint
    fetch(`${API_BASE_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (res.ok) {
          setState("success");
          return;
        }
        // Parse the error code from the response body
        const body = await res.json().catch(() => ({}));
        const code: string = body?.message || "";
        if (code === "VERIFICATION_TOKEN_EXPIRED") {
          setState("expired");
        } else {
          setState("invalid");
        }
      })
      .catch(() => setState("error"));
  }, []);

  /**
   * Navigate to the home page so the user can open the login modal from the header.
   */
  const goHome = () => {
    window.location.href = "/";
  };

  /**
   * Ask the backend for a fresh verification link using the expired token.
   *
   * The button is replaced by the confirmation once a request succeeds, so a user
   * cannot sit on this page firing off mails; the backend enforces its own cooldown
   * regardless and reports the remaining seconds if they reload and try again.
   */
  const handleResend = async () => {
    if (!token) return;
    setResendLoading(true);
    setResendInfo(null);
    try {
      const language = i18n.language?.startsWith("tr") ? "tr" : "en";
      await resendVerificationEmail({ token }, language);
      setResendDone(true);
      setResendInfo(t("verify_resend_sent"));
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      setResendInfo(parseApiError(message, t, "verify_resend_error"));
    } finally {
      setResendLoading(false);
    }
  };

  const renderContent = () => {
    switch (state) {
      case "loading":
        return (
          <>
            <div className="verify-email-icon verify-email-icon--loading">⏳</div>
            <h1 className="verify-email-title">{t("verify_email_loading_title")}</h1>
            <p className="verify-email-text">{t("verify_email_loading_text")}</p>
          </>
        );

      case "success":
        return (
          <>
            <div className="verify-email-icon">✅</div>
            <h1 className="verify-email-title">{t("verify_email_success_title")}</h1>
            <p className="verify-email-text">{t("verify_email_success_text")}</p>
            <button className="verify-email-btn" onClick={goHome}>
              {t("verify_email_success_btn")}
            </button>
          </>
        );

      case "expired":
        return (
          <>
            <div className="verify-email-icon">⏰</div>
            <h1 className="verify-email-title">{t("verify_email_expired_title")}</h1>
            <p className="verify-email-text">{t("verify_email_expired_text")}</p>

            {/* Primary action is getting a working link, not going home — going home
                leaves the user exactly where they started. Hidden once a new mail
                has gone out, so the confirmation is the last thing on screen. */}
            {!resendDone && (
              <button
                className="verify-email-btn"
                onClick={handleResend}
                disabled={resendLoading || !token}
              >
                {resendLoading ? t("verify_resend_loading") : t("verify_resend_button")}
              </button>
            )}

            {resendInfo && <p className="verify-email-resend-info">{resendInfo}</p>}

            <button className="verify-email-btn verify-email-btn--secondary" onClick={goHome}>
              {t("verify_email_expired_btn")}
            </button>
          </>
        );

      case "invalid":
        return (
          <>
            <div className="verify-email-icon">❌</div>
            <h1 className="verify-email-title">{t("verify_email_invalid_title")}</h1>
            <p className="verify-email-text">{t("verify_email_invalid_text")}</p>
            <button className="verify-email-btn" onClick={goHome}>
              {t("verify_email_invalid_btn")}
            </button>
          </>
        );

      case "error":
      default:
        return (
          <>
            <div className="verify-email-icon">⚠️</div>
            <h1 className="verify-email-title">{t("verify_email_error_title")}</h1>
            <p className="verify-email-text">{t("verify_email_error_text")}</p>
            <button className="verify-email-btn" onClick={goHome}>
              {t("verify_email_error_btn")}
            </button>
          </>
        );
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-card">
        <div className="verify-email-site-name">3 Korner 1 Penaltı</div>
        {renderContent()}
      </div>
    </div>
  );
};